import { useState, forwardRef, useImperativeHandle } from "react";
import toast from "react-hot-toast";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";
import { supabase } from "../../../../../supabase";
import { useMainContext } from "../../../../../context/useMainContext";

const DeleteConfirmDialog = forwardRef(function DeleteConfirmDialog({
  setOpenDotsMenu,
  setOpen,
}, ref) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const { selected, setSelected, setSelectAll, fetchDataTable, saveUndoData } = useMainContext();

  const fetchRelatedData = async (influencerIds) => {
    try {
      const [
        influencersData,
        freeChecksData,
        paidChecksData,
        internalReviewsData,
        freeCheckHistoryData,
        paidCheckHistoryData,
        statusHistoryData,
      ] = await Promise.all([
        supabase.from("influencers").select("*").in("id", influencerIds),
        supabase.from("influencer_free_checks").select("*").in("influencer_id", influencerIds),
        supabase.from("influencer_paid_checks").select("*").in("influencer_id", influencerIds),
        supabase.from("internal_reviews").select("*").in("influencer_id", influencerIds),
        supabase.from("free_check_history").select("*").in("influencer_id", influencerIds),
        supabase.from("paid_check_history").select("*").in("influencer_id", influencerIds),
        supabase.from("status_history").select("*").in("influencer_id", influencerIds),
      ]);

      const errors = [];
      if (influencersData.error) errors.push({ table: "influencers", error: influencersData.error });
      if (freeChecksData.error) errors.push({ table: "influencer_free_checks", error: freeChecksData.error });
      if (paidChecksData.error) errors.push({ table: "influencer_paid_checks", error: paidChecksData.error });
      if (internalReviewsData.error) errors.push({ table: "internal_reviews", error: internalReviewsData.error });
      if (freeCheckHistoryData.error) errors.push({ table: "free_check_history", error: freeCheckHistoryData.error });
      if (paidCheckHistoryData.error) errors.push({ table: "paid_check_history", error: paidCheckHistoryData.error });
      if (statusHistoryData.error) errors.push({ table: "status_history", error: statusHistoryData.error });

      if (errors.length > 0) {
        console.error("Errors fetching related data:", errors);
        throw new Error(`Failed to fetch data from ${errors.length} table(s)`);
      }

      return {
        influencers: influencersData.data || [],
        freeChecks: freeChecksData.data || [],
        paidChecks: paidChecksData.data || [],
        internalReviews: internalReviewsData.data || [],
        freeCheckHistory: freeCheckHistoryData.data || [],
        paidCheckHistory: paidCheckHistoryData.data || [],
        statusHistory: statusHistoryData.data || [],
      };
    } catch (error) {
      console.error("Error fetching related data:", error);
      throw error;
    }
  };

  const restoreDeletedData = async (backupData) => {
    try {
      const errors = [];

      if (backupData.influencers.length > 0) {
        const { error: influencersError } = await supabase
          .from("influencers")
          .insert(backupData.influencers);
        if (influencersError) errors.push({ table: "influencers", error: influencersError });
      }

      if (backupData.freeChecks.length > 0) {
        const { error: freeChecksError } = await supabase
          .from("influencer_free_checks")
          .insert(backupData.freeChecks);
        if (freeChecksError) errors.push({ table: "influencer_free_checks", error: freeChecksError });
      }

      if (backupData.paidChecks.length > 0) {
        const { error: paidChecksError } = await supabase
          .from("influencer_paid_checks")
          .insert(backupData.paidChecks);
        if (paidChecksError) errors.push({ table: "influencer_paid_checks", error: paidChecksError });
      }

      if (backupData.internalReviews.length > 0) {
        const { error: internalReviewsError } = await supabase
          .from("internal_reviews")
          .insert(backupData.internalReviews);
        if (internalReviewsError) errors.push({ table: "internal_reviews", error: internalReviewsError });
      }

      if (backupData.freeCheckHistory.length > 0) {
        const { error: freeCheckHistoryError } = await supabase
          .from("free_check_history")
          .insert(backupData.freeCheckHistory);
        if (freeCheckHistoryError) errors.push({ table: "free_check_history", error: freeCheckHistoryError });
      }

      if (backupData.paidCheckHistory.length > 0) {
        const { error: paidCheckHistoryError } = await supabase
          .from("paid_check_history")
          .insert(backupData.paidCheckHistory);
        if (paidCheckHistoryError) errors.push({ table: "paid_check_history", error: paidCheckHistoryError });
      }

      if (backupData.statusHistory.length > 0) {
        const { error: statusHistoryError } = await supabase
          .from("status_history")
          .insert(backupData.statusHistory);
        if (statusHistoryError) errors.push({ table: "status_history", error: statusHistoryError });
      }

      if (errors.length > 0) {
        console.error("Errors during restoration:", errors);
        throw new Error("Some data failed to restore");
      }

      await fetchDataTable();
      console.log("Undo restoration event:", backupData);
      toast.success(`${backupData.influencers.length} profiles restored.`);
    } catch (error) {
      console.error("Error restoring deleted data:", error);
      toast.error("Failed to restore deleted profiles.");
      throw error;
    }
  };

  const handleDeleteClick = () => {
    if (selected.length === 0) {
      toast.error("No profiles selected.");
      return;
    }
    setOpenDotsMenu(null);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    setDeleteDialogOpen(false);
    
    try {
      const influencerIds = selected
        .filter((item) => !item.isDetailRow)
        .map((inf) => inf.id);

      if (influencerIds.length === 0) {
        toast.error("No profiles selected for deletion.");
        return;
      }
      if (influencerIds.length !== selected.length) {
        console.warn(
          "Detail rows filtered out from deletion"
        );
      }

      const backupData = await fetchRelatedData(influencerIds);

      const { error: deleteError } = await supabase
        .from("influencers")
        .delete()
        .in("id", influencerIds);
      if (deleteError) {
        console.error("Error deleting influencers:", deleteError);
        toast.error("Failed to delete selected rows.");
        return;
      }

      console.log("Delete event:", backupData);

      toast.success(`${influencerIds.length} profiles deleted.`);
      setSelected([]);
      setSelectAll(false);
      await fetchDataTable();
      setOpen(false);

      saveUndoData({
        action: `${influencerIds.length} profile(s) deleted`,
        undoFunction: async () => {
          await restoreDeletedData(backupData);
        },
        redoFunction: async () => {
          const { error: redoDeleteError } = await supabase
            .from("influencers")
            .delete()
            .in("id", influencerIds);
          if (redoDeleteError) {
            console.error("Error re-deleting influencers:", redoDeleteError);
            toast.error("Failed to re-delete profiles.");
            return;
          }
          console.log("Redo delete event:", backupData);
          await fetchDataTable();
          toast.success(`${influencerIds.length} profiles deleted again.`);
        },
      });
    } catch (error) {
      console.error("Unexpected error during deletion:", error);
      toast.error("An unexpected error occurred during deletion.");
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  useImperativeHandle(ref, () => ({
    handleDeleteClick,
  }));

  return (
    <Dialog
      open={deleteDialogOpen}
      onClose={handleDeleteCancel}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Confirm Deletion</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Are you sure you want to delete {selected.length > 1 ? `these ${selected.length} partners` : 'this partner'}? This action cannot be undone.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleDeleteCancel} variant="outlined" disableElevation>
          Cancel
        </Button>
        <Button onClick={handleDeleteConfirm} variant="contained" color="error" disableElevation>
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
});

export default DeleteConfirmDialog;

